import {
  AudiotrackOutlined,
  DeleteOutlineRounded,
  InsertDriveFileOutlined,
  Search,
  UploadOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CardActions,
  CardContent,
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  Tooltip,
  Typography,
} from "@mui/material";
import styled from "@emotion/styled";
import { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import AccessDenied from "../../../components/access_denied/access_denied";
import Loading from "../../../components/loading/loading";
import OrgSelector from "../../../components/org_selector/org_selector";
import { ToolbarButton } from "../../../components/toolbar_button/toolbar_button";
import FilterIcon from "../../../assets/filter_icon.svg";
import SortIcon from "../../../assets/sort.svg";
import { useSelectedOrgStore } from "../../../store/use-selected-org-store";
import CmsLayout from "../../../layouts/cms/cms_layout";
import MainLayout from "../../../layouts/main/main_layout";
import { useFeedback } from "../../../providers/feedback/feeedback";
import { queryClient } from "../../../providers/query/query";
import useUser from "../../../providers/user/user_provider";
import { deleteMedia, getMediaList, MAX_MEDIA_FILE_SIZE_BYTES, MAX_MEDIA_FILE_SIZE_LABEL, uploadMedia } from "../../../services/cms/cms";
import DeleteCmsItemDialog from "../components/delete_cms_item/delete_cms_item";
import FilterMedia, { MediaFilterValues, MediaTypeFilter } from "../components/filter_media/filter_media";
import MediaThumbnail from "../components/media_thumbnail/media_thumbnail";
import MediaUploadDialog from "../components/media_upload_dialog/media_upload_dialog";
import SortMediaPopup, {
  DEFAULT_MEDIA_SORT_OPTION,
  MediaSortOption,
  mediaSortOptionLabel,
  mediaSortOptionToParams,
} from "../components/sort_media_popup/sort_media_popup";
import { TableFooterPagination } from "../../users/components/table_toolkit/table_toolkit";
import { redLight, red } from "../../../theme/colors";
import { Media } from "../models/cms";

// The list endpoint only supports an exact mime_type match, not a "family"
// (image/video/audio) filter, so that filtering happens client-side here.
function getMediaTypeFamily(mimeType?: string | null): Exclude<MediaTypeFilter, "all"> {
  if (mimeType?.startsWith("image/")) return "image";
  if (mimeType?.startsWith("video/")) return "video";
  if (mimeType?.startsWith("audio/")) return "audio";
  return "other";
}

const ROWS_PER_PAGE = 12;

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    "&.Mui-focused fieldset": { borderColor: "transparent" },
    "&:hover fieldset": { borderColor: "transparent" },
    "& fieldset": { borderColor: "transparent" },
  },
}));

export default function MediaLibrary() {
  const { hasPermission } = useUser();
  const { showSnackbar } = useFeedback();
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [mediaTypeFilter, setMediaTypeFilter] = useState<MediaTypeFilter>("all");
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [sortOption, setSortOption] = useState<MediaSortOption>(DEFAULT_MEDIA_SORT_OPTION);
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Media | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const { selectedOrgId } = useSelectedOrgStore();

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchValue(searchValue), 500);
    return () => clearTimeout(handler);
  }, [searchValue]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchValue, mediaTypeFilter, dateFrom, dateTo, selectedOrgId, sortOption]);

  const fromDate = dateFrom?.format("YYYY-MM-DD");
  const toDate = dateTo?.format("YYYY-MM-DD");

  const { data, isLoading, isFetching } = useQuery(
    ["cms-media", page, debouncedSearchValue, mediaTypeFilter, selectedOrgId, sortOption, fromDate, toDate],
    async () => {
      const { sortBy, sortOrder } = mediaSortOptionToParams(sortOption);
      const response = await getMediaList({
        page,
        size: ROWS_PER_PAGE,
        search: debouncedSearchValue,
        orgId: selectedOrgId || undefined,
        sortBy,
        sortOrder,
        mediaType: mediaTypeFilter,
        fromDate,
        toDate,
      });
      return response?.data?.data;
    },
    { keepPreviousData: true }
  );

  const pagedList: Media[] = data?.list || [];
  const count = data?.total || 0;

  const filterCount = [mediaTypeFilter !== "all", !!dateFrom && !!dateTo].filter(Boolean).length;

  const handleApplyFilters = (values: MediaFilterValues) => {
    setMediaTypeFilter(values.mediaTypeFilter);
    setDateFrom(values.dateFrom);
    setDateTo(values.dateTo);
  };

  const handleUpload = (file: File, title: string, altText: string) => {
    setUploading(true);
    uploadMedia(file, title, altText)
      .then(() => {
        showSnackbar("Media uploaded successfully");
        queryClient.invalidateQueries({ queryKey: ["cms-media"] });
        setPendingFile(null);
      })
      .catch((e) => {
        showSnackbar(e?.response?.data?.error?.message || "Something went wrong");
      })
      .finally(() => setUploading(false));
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    deleteMedia(deleteTarget.id)
      .then(() => {
        showSnackbar("Media deleted successfully");
        queryClient.invalidateQueries({ queryKey: ["cms-media"] });
      })
      .catch((e) => {
        showSnackbar(e?.response?.data?.error?.message || "Media could not be deleted");
      })
      .finally(() => {
        setIsDeleting(false);
        setDeleteTarget(null);
      });
  };

  return (
    <MainLayout>
      {hasPermission("cms", "read") ? (
        <CmsLayout>
          <Box display={"flex"} flexDirection={"column"} sx={{ height: "100%" }}>
            <Box
              sx={{ p: 2 }}
              display="flex"
              flexWrap="wrap"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
            >
              <TextField
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                size="small"
                fullWidth
                sx={{ maxWidth: 420, backgroundColor: "#F5E6F5", borderRadius: 3, borderColor: "transparent" }}
                placeholder="Search Media"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "text.primary" }} />
                    </InputAdornment>
                  ),
                }}
              />

              <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5}>
                <OrgSelector lockToOwnOrg />
                <ToolbarButton icon={SortIcon} label={mediaSortOptionLabel(sortOption)} onClick={() => setSortDialogOpen(true)} />
                <ToolbarButton icon={FilterIcon} label="Filters" badgeCount={filterCount} onClick={() => setFilterDialogOpen(true)} />

                {hasPermission("cms", "create") && (
                  <Tooltip title={`Max file size: ${MAX_MEDIA_FILE_SIZE_LABEL}`}>
                    <Button
                      variant="contained"
                      disableElevation
                      component="label"
                      disabled={uploading}
                      startIcon={<UploadOutlined />}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      Upload Media
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            if (file.size > MAX_MEDIA_FILE_SIZE_BYTES) {
                              showSnackbar(`File is too large. Maximum allowed size is ${MAX_MEDIA_FILE_SIZE_LABEL}.`);
                            } else {
                              setPendingFile(file);
                            }
                          }
                          e.target.value = "";
                        }}
                      />
                    </Button>
                  </Tooltip>
                )}
              </Box>
            </Box>

            <CardContent sx={{ display: "flex", flexDirection: "column", flexGrow: 1, flex: 1 }}>
              {isLoading || isFetching ? (
                <Box display="flex" justifyContent={"center"} alignItems={"center"} height="100%">
                  <Loading />
                </Box>
              ) : pagedList.length === 0 ? (
                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" flex={1}>
                  <Typography>No Media Available</Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                    gap: 2,
                  }}
                >
                  {pagedList.map((item) => (
                    <Box key={item.id} sx={{ border: "1px solid #E0E0E0", borderRadius: 2, p: 1 }}>
                      {getMediaTypeFamily(item.mime_type) === "image" ? (
                        <MediaThumbnail
                          src={item.file_url}
                          alt={item.alt_text || item.title || ""}
                          sx={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 1 }}
                        />
                      ) : getMediaTypeFamily(item.mime_type) === "video" ? (
                        <Box
                          component="video"
                          src={item.file_url}
                          controls
                          sx={{ width: "100%", height: 120, borderRadius: 1, bgcolor: "#000" }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: "100%",
                            height: 120,
                            borderRadius: 1,
                            bgcolor: "#F5F5F5",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {getMediaTypeFamily(item.mime_type) === "audio" ? (
                            <AudiotrackOutlined sx={{ color: "#9E9E9E", fontSize: 40 }} />
                          ) : (
                            <InsertDriveFileOutlined sx={{ color: "#9E9E9E", fontSize: 40 }} />
                          )}
                        </Box>
                      )}
                      <Typography variant="body2" fontWeight={600} noWrap mt={1}>
                        {item.title || item.file_path}
                      </Typography>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          {item.width && item.height ? `${item.width}x${item.height}` : ""}
                        </Typography>
                        {hasPermission("cms", "delete") && (
                          <Tooltip title="Delete media">
                            <IconButton
                              size="small"
                              onClick={() => setDeleteTarget(item)}
                              sx={{ bgcolor: redLight, "&:hover": { bgcolor: "#FBD9D6" } }}
                            >
                              <DeleteOutlineRounded fontSize="small" sx={{ color: red }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
            {count > 0 && (
              <CardActions sx={{ px: 3, pb: 3 }}>
                <Box display="flex" width="100%">
                  <TableFooterPagination
                    count={count}
                    page={page - 1}
                    onPageChange={(_, newPage) => setPage(newPage + 1)}
                    summaryText={`Showing ${count ? (page - 1) * ROWS_PER_PAGE + 1 : 0} to ${Math.min(page * ROWS_PER_PAGE, count)} of ${count} entries`}
                    rowsPerPage={ROWS_PER_PAGE}
                  />
                </Box>
              </CardActions>
            )}
          </Box>
          <DeleteCmsItemDialog
            open={!!deleteTarget}
            itemName={deleteTarget?.title || undefined}
            isDeleting={isDeleting}
            onClose={() => setDeleteTarget(null)}
            onConfirm={handleDeleteConfirm}
          />
          <MediaUploadDialog
            file={pendingFile}
            isUploading={uploading}
            onClose={() => setPendingFile(null)}
            onConfirm={(title, altText) => pendingFile && handleUpload(pendingFile, title, altText)}
          />
          {filterDialogOpen && (
            <FilterMedia
              open={filterDialogOpen}
              onClose={() => setFilterDialogOpen(false)}
              mediaTypeFilter={mediaTypeFilter}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onApply={handleApplyFilters}
            />
          )}
          <SortMediaPopup
            open={sortDialogOpen}
            onClose={() => setSortDialogOpen(false)}
            sortOption={sortOption}
            setSortOption={setSortOption}
          />
        </CmsLayout>
      ) : (
        <Box display="flex" flex={1} width="100%" height="100%">
          <AccessDenied />
        </Box>
      )}
    </MainLayout>
  );
}
